function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
import { useNotifyManager } from '../composables/notify-manager.js';

/**
 * Configuration options for the auth link notify plugin.
 *
 * @typedef {Object} AuthLinkNotifyOptions
 * @property {Function} isUserAuthenticated - Function that returns whether the user is authenticated.
 * @property {Object<string, {name: string, domain: string}>} strategies - Passport strategies for authentication.
 * @property {boolean} force - Forces plugin installation even if process.env.CLIENT is false.
 */

/**
 * Plugin for processing authentication link query parameters in a client-side router.
 *
 * When installed, adds an `afterEach` navigation guard to the specified router instance. Designed specifically for
 * client-side environments with customizable behavior options.
 *
 * @namespace
 */
export var routerAuthNotifyPlugin = {
  /**
   * Installs router plugin for processing authentication link query parameters.
   *
   * Sets up an `afterEach` navigation guard on the provided router instance to process query parameters for
   * authentication links in a non-blocking manner using `setTimeout`.
   *
   * @param {import('vue-router').Router} router - Router instance to attach the plugin to.
   * @param {AuthLinkNotifyOptions} [options={}] - Configuration options for processing query parameters.
   * @returns {void}
   */
  install: function install(router) {
    var _options$strategies;
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    if (!process.env.CLIENT && !options.force) {
      return;
    }
    if (!options.isUserAuthenticated || typeof options.isUserAuthenticated !== 'function') {
      console.warn('\'isUserAuthenticated\' function is required as an option for the authentication/link notification, skipping plugin installation.');
      return;
    }
    (_options$strategies = options.strategies) !== null && _options$strategies !== void 0 ? _options$strategies : options.strategies = {};
    router.afterEach(to => {
      setTimeout(() => processAuthLinkQueryParams(router, options, to.query), 0);
    });
  }
};

/**
 * Processes authentication-related query parameters and handles authentication flow.
 *
 * @param {import('vue-router').Router} router - Router instance for navigation.
 * @param {AuthLinkNotifyOptions} options - Configuration options for parameter handling.
 * @param {Object} queryParams - Query parameters to process.
 * @param {string} [queryParams.type] - Type of authentication request.
 * @param {string|number} [queryParams.status] - Status code of authentication request.
 * @returns {Promise<void>}
 */
function processAuthLinkQueryParams(_x, _x2, _x3) {
  return _processAuthLinkQueryParams.apply(this, arguments);
}
/**
 * Validates document referrer against provided strategies.
 *
 * @param {AuthLinkNotifyOptions} options - Configuration with validation strategies.
 * @returns {boolean}
 */
function _processAuthLinkQueryParams() {
  _processAuthLinkQueryParams = _asyncToGenerator(function* (router, options, queryParams) {
    var shouldDisplayNotification = isReferrerValid(options);
    var statusCode = queryParams.status ? parseStatusCode(queryParams.status) : null;
    if (Number.isInteger(statusCode) && queryParams.type === 'login') {
      handleAuthParam(router, options, statusCode, shouldDisplayNotification);
    }
    if (Number.isInteger(statusCode) && queryParams.type === 'link') {
      handleLinkParam(router, options, statusCode, shouldDisplayNotification, queryParams);
    }
  });
  return _processAuthLinkQueryParams.apply(this, arguments);
}
function isReferrerValid(options) {
  if (!isBrowserContext() || !document.referrer || !document.referrer.startsWith('http')) {
    return false;
  }
  var referrer = new URL(document.referrer);
  var currentLocation = new URL(window.location.href);
  if (referrer.host === currentLocation.host) {
    return true;
  }
  return Object.values(options.strategies).some(s => s.domain === referrer.host);
}

/**
 * Checks if current context is a web browser environment.
 *
 * @returns {boolean}
 */
function isBrowserContext() {
  return typeof window !== 'undefined' && window.hasOwnProperty('document');
}

/**
 * Converts input parameter to a status code number.
 *
 * @param {string|number} param - Input value representing a status code.
 * @returns {number|null} Parsed finite number or null if invalid.
 */
function parseStatusCode(param) {
  var number = Number(param);
  return Number.isFinite(number) ? number : null;
}

/**
 * Handles authentication status parameter and notifies user of errors.
 *
 * @param {import('vue-router').Router} router - Router instance for managing routes.
 * @param {AuthLinkNotifyOptions} options - Authentication configuration and methods.
 * @param {number} statusCode - Current authentication state code.
 * @param {boolean} shouldDisplayNotification - Whether to display error notifications.
 * @returns {void}
 */
function handleAuthParam(router, options, statusCode, shouldDisplayNotification) {
  if (statusCode === 0 && !options.isUserAuthenticated() && shouldDisplayNotification) {
    useNotifyManager().notifyError('Login failed, the email may already be associated with an account using another login service.');
  }
  removeQueryParams(router, 'status', 'type', 'provider');
}

/**
 * Handles link parameter status and displays appropriate notifications.
 *
 * @param {import('vue-router').Router} router - Router instance for managing routes.
 * @param {AuthLinkNotifyOptions} options - Authentication configuration and methods.
 * @param {number} statusCode - Current authentication state code.
 * @param {boolean} shouldDisplayNotification - Whether to display error notifications.
 * @param {Object} queryParams - Query parameters containing provider information.
 * @param {string} [queryParams.provider] - Authentication provider key.
 * @returns {void}
 */
function handleLinkParam(router, options, statusCode, shouldDisplayNotification, queryParams) {
  var _options$strategies$p;
  var providerKey = queryParams.provider;
  var providerName = options === null || options === void 0 || (_options$strategies$p = options.strategies[providerKey]) === null || _options$strategies$p === void 0 ? void 0 : _options$strategies$p.name;
  var messagePrefix = providerName ? "Your ".concat(providerName) : 'Your';
  if (statusCode === 0 && shouldDisplayNotification) {
    useNotifyManager().notifyError("".concat(messagePrefix, " account could not be linked, please contact support for assistance."));
  }
  removeQueryParams(router, 'status', 'type', 'provider');
}

/**
 * Removes specified query parameters from current route.
 *
 * @param {import('vue-router').Router} router - Router instance for managing routes.
 * @param {...string} params - Query parameters to remove.
 * @returns {Promise<void>}
 */
function removeQueryParams(_x4) {
  return _removeQueryParams.apply(this, arguments);
}
function _removeQueryParams() {
  _removeQueryParams = _asyncToGenerator(function* (router) {
    var newQuery = _objectSpread({}, router.currentRoute.value.query);
    for (var _len = arguments.length, params = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      params[_key - 1] = arguments[_key];
    }
    for (var param of params) {
      delete newQuery[param];
    }
    yield router.replace({
      query: newQuery
    });
  });
  return _removeQueryParams.apply(this, arguments);
}